import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { Logger } from '@sondermind/datadog-logging';
import { HttpService } from '@sondermind/http';
import {
  IAPIFetchResponse,
  IAPIFetchRequestOptions,
  IAPIListResponse,
  IAPIListRequestOptions
} from '@sondermind/utilities/models-http';

import {
  ClientReferralMember,
  IClientReferralMemberApiResponse
} from '../models/client-referral-member.model';

@Injectable({
  providedIn: 'root'
})
export class ClientReferralMembersHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService,
    private logger: Logger
  ) {}

  archive$(
    crmIds: number[],
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<ClientReferralMember[] | null> {
    const data = { ids: crmIds };
    const params = this.http.generateListParams(opts);
    const url = `${this.configurationService.env.svcBase}/referrals/api/v1/client_referral_members/archive`;

    return this.http
      .post<IAPIFetchResponse<IClientReferralMemberApiResponse[]>>(url, data, params)
      .pipe(
        map((resp) => (resp?.data ?? []).map((member) => new ClientReferralMember(member))),
        this.http.handleHttpError()
      );
  }

  /**
   * Fetches a specific client referral member by ID from the API
   * @param id The ID of the client referral member to fetch
   * @param opts Optional request options
   * @returns Observable of ClientReferralMember or null if response data is missing
   */
  fetchById(id: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<ClientReferralMember | null> {
    const params = this.http.generateFetchParams(opts);
    const url = `${this.configurationService.env.svcBase}/referrals/api/v1/client_referral_members/${id}`;

    return this.http
      .get<IAPIFetchResponse<IClientReferralMemberApiResponse>>(url, params)
      .pipe(
        map((resp) => (resp?.data ? new ClientReferralMember(resp.data) : null)),
        this.http.handleHttpErrorStrict()
      );
  }

  /**
   * Fetches all client referral members with automatic pagination
   * Gracefully handles pagination errors - returns partial data if later pages fail
   * @param opts Optional request options
   * @returns Observable of all ClientReferralMembers across all pages (or partial if pagination fails)
   */
  fetchAll(opts: Partial<IAPIListRequestOptions> = {}): Observable<ClientReferralMember[]> {
    return this.fetchPage(opts).pipe(
      expand((response) => {
        if (!response.meta.links.next) {
          return EMPTY;
        }

        // Fetch next page with error handling to prevent pagination failures from breaking the entire stream
        return this.fetchPageByUrl(response.meta.links.next).pipe(
          catchError((error: unknown) => {
            this.logger.error('Pagination failed - returning data fetched so far', {
              failedUrl: response.meta.links.next,
              error
            });
            // Stop pagination by returning EMPTY, but allow the stream to complete with data collected so far
            return EMPTY;
          })
        );
      }),
      // Collect all pages and flatten the data arrays
      reduce((allMembers: ClientReferralMember[], response) => allMembers.concat(response.data), [])
    );
  }

  /**
   * Fetches a single page of client referral members
   * @param opts Optional request options
   * @returns Observable of IAPIListResponse with ClientReferralMembers
   */
  fetchPage(opts: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<ClientReferralMember>> {
    const params = this.http.generateListParams(opts);
    const url = `${this.configurationService.env.svcBase}/referrals/api/v1/client_referral_members`;

    return this.http
      .get<IAPIListResponse<IClientReferralMemberApiResponse>>(url, params)
      .pipe(
        map((resp) => ({
          ...resp,
          data: (resp?.data ?? []).map((member) => new ClientReferralMember(member))
        })),
        this.http.handleHttpErrorStrict()
      );
  }

  /**
   * Fetches a page by direct URL (used for pagination)
   * @param url The full URL to fetch
   * @returns Observable of IAPIListResponse with ClientReferralMembers
   */
  private fetchPageByUrl(url: string): Observable<IAPIListResponse<ClientReferralMember>> {
    return this.http
      .get<IAPIListResponse<IClientReferralMemberApiResponse>>(url)
      .pipe(
        map((resp) => ({
          ...resp,
          data: (resp?.data ?? []).map((member) => new ClientReferralMember(member))
        })),
        this.http.handleHttpErrorStrict()
      );
  }
}
